/**
 * Per-channel audio capture source.
 *
 * Encapsulates everything that touches `MediaStream`,
 * `MediaStreamTrack`, and `MediaStreamTrackProcessor` for one channel.
 * The orchestrating AudioChannel asks for AudioData frames via a
 * delegate; this module hides:
 *
 *   - The acquisition path (`getUserMedia` for mic,
 *     `getDisplayMedia` for system loopback, with fallback).
 *   - Per-source constraints (echo/AGC/NS/voice-isolation tuning).
 *   - The track's `onended` handler and the "this isn't ours anymore"
 *     guard for concurrent stop()/restart() races.
 *   - The MediaStreamTrackProcessor read loop, including pause
 *     plumbing and reader cancel on teardown.
 *
 * Two concrete implementations:
 *   - `MicCaptureSource`    — single audio track from getUserMedia
 *   - `SystemCaptureSource` — single audio track from getDisplayMedia
 *
 * The base class owns the track-processor read loop because both
 * sources use it identically; subclasses provide acquisition and
 * post-acquire wiring (e.g. logging the track settings the OS landed
 * on).
 */
import {
  formatAudioRouting,
  looksBluetoothLabel,
  looksBuiltInMicLabel,
  looksUnwantedAutoMicLabel,
  publishAudioDebugEvent,
  publishAudioDebugRouting,
  snapshotAudioRouting,
} from './audio-debug'
import { TARGET_SAMPLE_RATE, type ChannelLabel } from './types'
import { avoidHfpMicsEnabled } from '@shared/store/impl/audioSettings'
import * as Sentry from '@sentry/electron/renderer'
import { compareCodeUnits } from '@renderer/utils/string'

/** One stage of a debug capture bundle. The bundle is an ordered
 *  array of these — the audio-runtime renderer downloads each as
 *  its own .wav so you can A/B compare them in any audio app. */
export interface DebugCaptureStage {
  /** Short identifier baked into the downloaded filename. Stable
   *  across runs so you can grep test results. */
  name: string
  /** Human-readable explanation of where in the pipeline this
   *  stage was tapped. Surfaces in the renderer log next to the
   *  download URL so you don't have to remember the pipeline order. */
  description: string
  /** Self-contained WAV (header + data). Empty `Uint8Array` when
   *  nothing has been recorded yet (e.g. dump fired in the first
   *  ~10 ms of a stream). */
  wav: Uint8Array
  /** Header sample rate the WAV was written at. Useful for logs
   *  without re-parsing the WAV. */
  sampleRate: number
  /** Audio duration in seconds, derived from data byte count. */
  durationSec: number
}

/** Reasons a capture source delegate is told the track ended. */
export type CaptureEndedReason =
  /** Programmatic teardown via stop() — not a recovery trigger. */
  | 'released'
  /** OS / browser tore the track down (track.onended fired) — the
   *  channel may want to restart capture. */
  | 'unexpected'
  /** The read loop ended because the readable stream returned done.
   *  Treated as `unexpected` for recovery purposes. */
  | 'reader-done'
  /** The read loop threw and the channel already reported it to Sentry
   *  (`audio-capture-readloop-error`). Treated as `unexpected` for recovery,
   *  but callers must NOT re-report it as a track ending — that would double
   *  up the telemetry and conflate a pipeline bug with an OS track teardown.
   *  Produced by `AudioChannel.handleCaptureError`, not the capture source. */
  | 'read-error'

/** Caller-provided callbacks. Always invoked synchronously; the
 *  capture source never holds delegate calls pending across `await`. */
export interface CaptureSourceDelegate {
  /** A new AudioData frame is available. Caller MUST close the frame
   *  when done with it. Skipped while paused. */
  onFrame(frame: AudioData): void
  /** The underlying track ended. The reason field disambiguates
   *  programmatic stop from OS-level loss. The capture source
   *  releases its own resources before this fires. */
  onEnded(reason: CaptureEndedReason): void
  /** Read loop saw an unexpected error. The capture source releases
   *  its own resources before this fires. Reader cancellation
   *  (TypeError / AbortError) is swallowed and reported via
   *  `onEnded('released')` instead. */
  onError(err: unknown): void
}

/** Snapshot of the source's current state for diagnostics. */
export interface CaptureSnapshot {
  liveTrack: boolean
  trackInfo: string
  claimedSampleRate: number
}

/** Base class for all audio capture sources. Handles the read loop and
 *  shared lifecycle; subclasses provide acquisition.
 *
 *  Most captures are run through a Web Audio graph configured for mono
 *  output at TARGET_SAMPLE_RATE. The microphone intentionally opts out:
 *  Chromium issue #687574 means connecting a mic stream to
 *  createMediaStreamSource can silently break native echo cancellation.
 *  The PCM pipeline therefore remains responsible for resampling any
 *  raw-track frames a subclass chooses to expose. */
export abstract class AudioCaptureSource {
  protected stream: MediaStream | null = null
  /** AudioContext used to wrap the raw capture into a mono
   *  TARGET_SAMPLE_RATE stream. Closed during teardown. */
  private wrapContext: AudioContext | null = null
  private wrapSourceNode: MediaStreamAudioSourceNode | null = null
  protected processor: MediaStreamTrackProcessor<AudioData> | null = null
  protected reader: ReadableStreamDefaultReader<AudioData> | null = null
  /** Set to true once stop() / dispose() begins. Guards the read loop
   *  so a frame that arrives after stop can't be dispatched, and
   *  guards onended so a programmatic stop doesn't trigger
   *  "unexpected" recovery. */
  protected stopped = false
  /** Set when the caller pauses upstream processing. Frames are read
   *  and immediately closed (so the processor doesn't backpressure)
   *  but not delivered to the delegate. */
  protected paused = false
  /** Set after the first `delegate.onEnded` call so we never double-fire
   *  when the OS `track.onended` and a natural reader-done both happen
   *  for the same track. The `stopped` guard on the onended handler
   *  catches the program-stop side, but onended/reader-done can race
   *  on a real OS-side track loss with no programmatic stop in between. */
  protected endedDispatched = false

  abstract readonly label: ChannelLabel
  /** Concrete sources implement the OS-specific acquisition path
   *  (`getUserMedia` / `getDisplayMedia`). Returns the acquired
   *  MediaStream; throws on failure. */
  protected abstract acquireStream(): Promise<MediaStream>
  /** Optional hook for subclasses to do post-acquire wiring (e.g.
   *  applying additional constraints, logging settings). The base
   *  class always assigns the stream to `this.stream` first so this
   *  hook can read it. */
  protected async onAcquired(): Promise<void> {
    /* default: nothing */
  }

  /** When true (default), the base class wraps the raw track in a
   *  16 kHz mono Web Audio graph and the read loop sees normalised
   *  AudioData. When false, the raw track's MediaStreamTrackProcessor
   *  is used directly; `AudioPcmPipeline` still resamples to
   *  TARGET_SAMPLE_RATE before bytes are sent downstream.
   *
   *  Mic overrides this to preserve Chromium's native AEC pipeline
   *  (see `MicCaptureSource.useWebAudioWrap`). System loopback uses
   *  the wrap because it benefits from Chromium's downmix/resampler and
   *  does not rely on microphone AEC. */
  protected useWebAudioWrap(): boolean {
    return true
  }

  /** Per-frame dispatch hook. Default delivers the frame straight
   *  to the delegate; `AudioPcmPipeline` owns any remaining
   *  normalisation. Subclasses may override this to interpose their
   *  own processing. If they do, the subclass owns the input frame's
   *  lifetime: it MUST `.close()` rawFrame before returning, and any
   *  synthesised frames it hands to the delegate follow the standard
   *  delegate contract (delegate closes them via the channel's
   *  pipeline). */
  protected dispatchRawFrame(rawFrame: AudioData, delegate: CaptureSourceDelegate): void {
    delegate.onFrame(rawFrame)
  }

  /** Acquire the stream, wire the read loop, and start dispatching
   *  frames to the delegate. Throws if acquisition fails. After
   *  start(), the source owns its MediaStream and will release it on
   *  stop() or when the track ends. */
  async start(delegate: CaptureSourceDelegate): Promise<void> {
    if (this.stopped) {
      throw new Error(`Cannot start a stopped ${this.label} capture source`)
    }
    const stream = await this.acquireStream()

    // A concurrent stop() may have set `stopped = true` while we were
    // awaiting acquisition (e.g. the user toggled the runtime off
    // during the permission dialog). Release the freshly acquired
    // tracks instead of crashing on a stale ref.
    if (this.stopped) {
      stream.getTracks().forEach((t) => t.stop())
      throw new Error(`${this.label} capture source was stopped during acquisition`)
    }

    this.stream = stream
    try {
      await this.onAcquired()
    } catch (err) {
      // `onAcquired` runs non-critical post-acquire wiring (routing
      // snapshot publish, settings logging, debug events). Critical
      // post-acquire work — system loopback's `applyConstraints` —
      // has its own try/catch with appropriate severity. We swallow
      // here so a flaky routing snapshot doesn't tear down a working
      // capture, but Sentry-tag the throw so the failure mode is
      // visible in telemetry instead of disappearing into a console
      // warn no one greps for.
      console.warn(`[AudioTranscriptionService:${this.label}] onAcquired hook failed:`, err)
      Sentry.captureException(err instanceof Error ? err : new Error(`onAcquired threw: ${String(err)}`), {
        tags: {
          component: 'audio-transcription-service',
          channel: this.label,
          errorId: 'audio-onacquired-failed',
        },
      })
    }

    if (this.stopped) {
      this.releaseStream()
      throw new Error(`${this.label} capture source was stopped during onAcquired`)
    }

    const audioTrack = stream.getAudioTracks()[0]
    if (!audioTrack) {
      this.releaseStream()
      throw new Error(`${this.label} capture source acquired stream with no audio tracks`)
    }

    audioTrack.onended = () => {
      // Programmatic track.stop() (releaseStream) does NOT fire
      // 'ended' per spec, so reaching this handler means the OS or
      // browser tore the track down on us. The `stopped` flag
      // disambiguates the rare case where releaseStream and an OS
      // ended event race. Wired to the RAW capture track because the
      // destination track of the Web Audio wrap stays "live"
      // indefinitely — the only way to hear about underlying source
      // loss is from the source track.
      if (this.stopped) return
      console.warn(`[AudioTranscriptionService:${this.label}] track ended unexpectedly`)
      // Surface unexpected track loss to the overlay; CATAP/device-route
      // restarts also funnel through this path.
      publishAudioDebugEvent({
        atMs: Date.now(),
        channel: this.label,
        severity: 'warn',
        message: 'track ended unexpectedly (OS / browser tore the underlying capture down)',
      })
      this.releaseStream()
      this.dispatchEnded(delegate, 'unexpected')
    }

    // Wrap most captures in a 16 kHz mono Web Audio graph; subclasses
    // can opt out when the raw track has to preserve browser processing
    // such as native mic AEC.
    let procTrack: MediaStreamTrack
    if (this.useWebAudioWrap()) {
      let wrapStream: MediaStream
      try {
        wrapStream = await this.setupWrap(stream)
      } catch (err) {
        this.releaseStream()
        throw new Error(`${this.label} capture source failed to set up Web Audio wrap: ${String(err)}`)
      }

      if (this.stopped) {
        this.releaseStream()
        throw new Error(`${this.label} capture source was stopped during Web Audio wrap setup`)
      }

      const wrapped = wrapStream.getAudioTracks()[0]
      if (!wrapped) {
        this.releaseStream()
        throw new Error(`${this.label} capture source Web Audio wrap produced no audio tracks`)
      }
      procTrack = wrapped
    } else {
      console.log(`[AudioTranscriptionService:${this.label}] Web Audio wrap bypassed — PCM pipeline will normalise`)
      procTrack = audioTrack
    }

    this.processor = new MediaStreamTrackProcessor({ track: procTrack })
    this.reader = this.processor.readable.getReader()

    void this.runReadLoop(delegate)
  }

  /** Build a 16 kHz mono Web Audio graph in front of the raw capture
   *  stream and return the destination MediaStream. The destination
   *  track is what MediaStreamTrackProcessor reads, so wrapped
   *  sources reach the pipeline as mono frames at TARGET_SAMPLE_RATE
   *  regardless of what the source delivered.
   *
   *  `channelCountMode: 'explicit'` is mandatory — without it the
   *  destination expands to match the source channel count instead
   *  of honouring `channelCount = 1`. `'speakers'` selects the
   *  spec's standard stereo→mono downmix formula
   *  (`output = 0.5 * (L + R)`). */
  private async setupWrap(stream: MediaStream): Promise<MediaStream> {
    const ctx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE, latencyHint: 'interactive' })
    const sourceNode = ctx.createMediaStreamSource(stream)
    const destinationNode = ctx.createMediaStreamDestination()
    destinationNode.channelCount = 1
    destinationNode.channelCountMode = 'explicit'
    destinationNode.channelInterpretation = 'speakers'
    sourceNode.connect(destinationNode)
    // Some Electron / Chromium combinations create the context in a
    // 'suspended' state. resume() is harmless when already running.
    if (ctx.state === 'suspended') {
      await ctx.resume()
    }
    this.wrapContext = ctx
    this.wrapSourceNode = sourceNode
    const trackSettings = stream.getAudioTracks()[0]?.getSettings() as
      | { sampleRate?: number; channelCount?: number }
      | undefined
    console.log(
      `[AudioTranscriptionService:${this.label}] Web Audio wrap: source=${trackSettings?.channelCount ?? '?'}ch @ ${trackSettings?.sampleRate ?? '?'}Hz → 1ch @ ${TARGET_SAMPLE_RATE}Hz`,
    )
    return destinationNode.stream
  }

  private async runReadLoop(delegate: CaptureSourceDelegate): Promise<void> {
    try {
      while (true) {
        if (this.stopped) break
        const { value: frame, done } = await this.reader!.read()
        if (done) break
        // Re-check stopped AFTER the read returns. Without this, a
        // frame in flight at the moment stop() was called would be
        // dispatched into a tearing-down channel — the channel
        // defends with its own disposed check today, but explicitly
        // closing+breaking here keeps the guarantee local: a stopped
        // capture source NEVER hands frames to the delegate.
        if (this.stopped) {
          frame.close()
          break
        }
        if (this.paused) {
          frame.close()
          continue
        }
        // Hand off via the dispatch hook. The hook owns the frame's
        // lifetime from this point: the default impl forwards to
        // delegate.onFrame (which closes via the channel's pipeline
        // try/finally); subclass overrides close the raw frame
        // themselves and may synthesise new AudioData for the
        // delegate. Either way, the read loop never touches the
        // frame again. Belt-and-suspenders: catch a delegate throw
        // and close defensively, since Chromium logs a warning when
        // an AudioData is GC'd unclosed and the default delegate
        // contract is "callee closes."
        try {
          this.dispatchRawFrame(frame, delegate)
        } catch (err) {
          try {
            frame.close()
          } catch {
            // ignore double-close (override may have already closed)
          }
          throw err
        }
      }
      // Reader returned done naturally — treat as a soft end so the
      // channel can decide whether to restart. Use the dispatchEnded
      // guard so we don't fire after track.onended already did.
      if (!this.stopped) {
        this.releaseStream()
        this.dispatchEnded(delegate, 'reader-done')
      }
    } catch (err) {
      // Reader was cancelled during teardown — expected. We treat
      // TypeError as a cancellation marker ONLY when stopped is set;
      // otherwise it's almost certainly a real bug in the delegate
      // chain (e.g. typo'd property access in pipeline.process) and
      // swallowing it would silently drop the read loop.
      const isReaderCancelled =
        (err as DOMException)?.name === 'AbortError' || (this.stopped && err instanceof TypeError)
      if (isReaderCancelled) {
        return
      }
      if (!this.stopped) {
        console.error(`[AudioTranscriptionService:${this.label}] read loop error:`, err)
        this.releaseStream()
        // Gate via the same dispatchEnded flag so an OS track.onended
        // that fires alongside the read-loop error doesn't produce
        // two delegate signals for one track loss.
        if (!this.endedDispatched) {
          this.endedDispatched = true
          delegate.onError(err)
        }
      }
    }
  }

  /** Dispatch `onEnded` to the delegate exactly once per source
   *  lifetime. The OS `track.onended` event and a natural reader-done
   *  can both fire for the same track loss; the read-loop error path
   *  also routes through `endedDispatched`. We want the channel to
   *  see one recovery trigger, not two. */
  private dispatchEnded(delegate: CaptureSourceDelegate, reason: CaptureEndedReason): void {
    if (this.endedDispatched) return
    this.endedDispatched = true
    delegate.onEnded(reason)
  }

  /** Pause frame delivery without releasing the underlying capture.
   *  Frames are still read from the processor (so it doesn't apply
   *  backpressure) but immediately closed. Idempotent. */
  pause(): void {
    this.paused = true
  }

  /** Resume frame delivery. Idempotent. */
  resume(): void {
    this.paused = false
  }

  /** Programmatic teardown. Cancels the reader, releases the stream
   *  (which fires `onended` synchronously per Chromium impl, but the
   *  `stopped` guard above suppresses the `unexpected` callback).
   *  Idempotent. */
  stop(): void {
    if (this.stopped) return
    this.stopped = true
    this.releaseStream()
  }

  protected releaseStream(): void {
    if (this.reader) {
      this.reader.cancel().catch(() => {})
      this.reader = null
    }
    if (this.processor) {
      this.processor = null
    }
    // Tear the Web Audio wrap down BEFORE stopping the raw stream's
    // tracks. Disconnecting the source node first ensures Chromium
    // doesn't briefly try to pump a closed track through the still-
    // live destination. The order is idempotent — releaseStream()
    // can be called multiple times during teardown races.
    if (this.wrapSourceNode) {
      try {
        this.wrapSourceNode.disconnect()
      } catch {
        // ignore
      }
      this.wrapSourceNode = null
    }
    if (this.wrapContext) {
      this.wrapContext.close().catch(() => {
        // ignore — context may already be closed
      })
      this.wrapContext = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => {
        // Detach onended so a release-time 'ended' fire (some Chromium
        // builds do emit it on programmatic stop) can't reach the
        // listener after we've already started teardown. The `stopped`
        // guard would have skipped the callback anyway, but this keeps
        // teardown unambiguous.
        if (t.kind === 'audio') t.onended = null
        try {
          t.stop()
        } catch {
          // ignore
        }
      })
      this.stream = null
    }
  }

  hasLiveTrack(): boolean {
    return this.stream?.getAudioTracks().some((t) => t.readyState === 'live') ?? false
  }

  /** The primary audio track if a stream is currently held, else null.
   *  The stream is released on track-end (`onended` → `releaseStream`), so
   *  this returns null once an unexpected ending has fired — callers gathering
   *  post-mortem diagnostics must tolerate null. */
  getPrimaryTrack(): MediaStreamTrack | null {
    return this.stream?.getAudioTracks()[0] ?? null
  }

  inspect(): CaptureSnapshot {
    const tracks = this.stream?.getAudioTracks() ?? []
    const trackInfo = tracks.map((t) => `${t.readyState}/muted=${t.muted}/enabled=${t.enabled}`).join(';') || 'none'
    const settings = tracks[0]?.getSettings()
    return {
      liveTrack: this.hasLiveTrack(),
      trackInfo,
      claimedSampleRate: settings?.sampleRate ?? 0,
    }
  }

  /** Per-stage debug audio captures owned by this source. The base
   *  implementation returns `[]` because the only "stage" the
   *  current pipeline exposes by default is the post-normalisation
   *  output, which is already captured by `AudioChannel`. Sources
   *  with additional pre-pipeline taps can override this to expose
   *  intermediate stages. */
  dumpDebugCaptures(): Array<DebugCaptureStage> {
    return []
  }

  /** Drop any source-owned debug captures. Called by `AudioChannel`
   *  when the user requests `clearDebugBuffer()` so the next dump
   *  starts from a known-empty state. */
  clearDebugCaptures(): void {
    // base: nothing to clear
  }
}

// ── Microphone ────────────────────────────────────────────────────

/**
 * Microphone capture via `navigator.mediaDevices.getUserMedia`.
 *
 * Constraints:
 *   - voiceIsolation: opt into Apple's macOS Voice Isolation pipeline
 *     (gated by the MediaCaptureVoiceIsolation feature flag in
 *     main.ts). On macOS 14.4+ this routes mic capture through
 *     AUVoiceProcessingIO with the extended voice-processing mode,
 *     which suppresses background sounds — including some other-app
 *     audio — at the OS layer. Unlike SystemLoopbackAsAecReference
 *     this does NOT require the audio service helper to have the
 *     CATAP entitlement, so it works in unsigned dev builds. We
 *     always request it (along with echo cancellation) for both
 *     meeting and dictation modes — neither triggers the macOS
 *     screen-recording indicator anymore, and the cleanup is a UX
 *     win in both modes.
 */
export class MicCaptureSource extends AudioCaptureSource {
  readonly label: ChannelLabel = 'mic'

  /** Mic device the caller asked for, or `undefined` for "default". */
  private readonly requestedDeviceId: string | undefined
  /** Device the auto-switch resolved (or `undefined` if no override
   *  was needed). Surfaced so that downstream diagnostics + the
   *  `track.label` log line carry the actual device that was opened
   *  rather than just "default". */
  private resolvedDeviceId: string | undefined
  /** Set when the auto-switch fired — used by `onAcquired()` to log
   *  the override in the same line as the routing snapshot, so the
   *  cause/effect is obvious in post-mortem grep. */
  private autoSwitchOverride: { from: string; to: string } | null = null

  constructor(deviceId?: string) {
    super()
    this.requestedDeviceId = deviceId
  }

  protected useWebAudioWrap(): boolean {
    // Keep the mic on the raw MediaStreamTrackProcessor path. Routing
    // a getUserMedia mic track through createMediaStreamSource can
    // detach Chromium's native echo-cancellation processing
    // (Chromium issue #687574). AudioPcmPipeline handles raw mic
    // sample-rate normalisation before PCM leaves the renderer.
    return false
  }

  /** The name the OS gives the device actually opened ("AirPods Pro", "MacBook Pro Microphone"), once acquired. */
  get deviceLabel(): string | undefined {
    return this.stream?.getAudioTracks()[0]?.label
  }

  protected async acquireStream(): Promise<MediaStream> {
    // HFP avoidance: when "default mic" is the same Bluetooth device as
    // the default output, opening it degrades loopback capture. Resolve
    // to a non-Bluetooth input only for the implicit default choice.
    const overrideId = !this.requestedDeviceId ? await pickNonBluetoothMicForHfpAvoidance() : null
    if (overrideId) {
      this.autoSwitchOverride = { from: overrideId.fromLabel, to: overrideId.toLabel }
      this.resolvedDeviceId = overrideId.deviceId
    } else {
      this.resolvedDeviceId = this.requestedDeviceId
    }

    // Cast through `unknown` because `voiceIsolation` isn't in lib.dom.d.ts.
    //
    // Use plain browser-scope AEC. Chromium's all-processes AEC can spin
    // up a competing CATAP consumer, so other-app speaker cleanup relies
    // on Apple's voice-processing path instead.
    const audioConstraints = {
      echoCancellation: 'all',
      noiseSuppression: true,
      autoGainControl: true,
      voiceIsolation: true,
    } as unknown as MediaTrackConstraints
    if (this.resolvedDeviceId) {
      ;(audioConstraints as MediaTrackConstraints).deviceId = { exact: this.resolvedDeviceId }
    }
    return navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
  }

  protected async onAcquired(): Promise<void> {
    const track = this.stream?.getAudioTracks()[0]
    if (!track) return
    const settings = track.getSettings() as Record<string, unknown>
    // Print the full settings dict so we can see which AEC mode /
    // voice pipeline the track actually settled on. JSON.stringify
    // because the renderer logger flattens objects to "[object
    // Object]" otherwise.
    const settingsJson = JSON.stringify(settings, (_k, v) => (typeof v === 'bigint' ? v.toString() : v))
    const capabilities = typeof track.getCapabilities === 'function' ? track.getCapabilities() : {}
    const capabilitiesJson = JSON.stringify(capabilities)
    // `track.label` is the human-readable device name macOS reported
    // to Chromium ("MacBook Pro Microphone", "AirPods Max", etc.).
    // Indispensable for triaging the HFP-degradation failure mode
    // without an extra grep through enumerateDevices() output.
    console.log(
      `[AudioTranscriptionService:mic] track settings: label="${track.label}" echoCancellation=${settings.echoCancellation} voiceIsolation=${settings.voiceIsolation ?? '(unset)'} echoCancellationMode=${settings.echoCancellationMode ?? '(unset)'} full=${settingsJson} capabilities=${capabilitiesJson}`,
    )
    // Routing snapshot at acquire time. We call this from the mic
    // path (in addition to system-loopback) because the mic capture
    // is itself a potential HFP trigger — if the default input is
    // physically the same Bluetooth device as the default output,
    // opening the mic here will force HFP mode and degrade any
    // concurrent system-audio loopback. Keeping the log next to
    // mic-acquire makes the cause/effect ordering obvious in the
    // post-mortem timeline.
    const routing = await snapshotAudioRouting()
    console.log(`[AudioTranscriptionService:mic] audio routing at acquire: ${formatAudioRouting(routing)}`)
    // Keep overlay routing/events warm for mid-session debugging.
    if (routing) {
      publishAudioDebugRouting(routing)
      // Event priority: explicit auto-switch, unresolved HFP risk, or
      // normal mic acquisition.
      const override = this.autoSwitchOverride
      const eventMessage: string = override
        ? `auto-switched mic from "${override.from}" → "${override.to}" to avoid HFP system-audio degradation`
        : routing.hfpTriggerRisk
          ? `mic acquired with HFP trigger risk (default in/out share groupId on "${routing.defaultInputLabel}")`
          : `mic acquired on "${track.label}"`
      publishAudioDebugEvent({
        atMs: Date.now(),
        channel: 'mic',
        severity: override ? 'warn' : routing.hfpTriggerRisk ? 'warn' : 'info',
        message: eventMessage,
      })
    }
  }
}

/**
 * If the OS's default audioinput is a Bluetooth device that is also
 * the default audiooutput (the `hfpTriggerRisk` predicate from
 * `snapshotAudioRouting()`), pick the first non-Bluetooth audioinput
 * to use instead. Returns `null` to mean "leave the device alone".
 *
 * Returns the resolved `deviceId` plus human-readable from/to labels
 * so the caller can both pass `deviceId` to `getUserMedia` and log
 * an explanatory message. Caller is also expected to:
 *   - Log loudly (console.warn) so the override shows up in
 *     post-mortem grep without needing the overlay enabled.
 *   - Emit a high-severity audio-debug event so the override is
 *     visible to anyone watching the overlay live.
 *
 * Behaviour is conservative — fails-open by returning `null` on:
 *   - `enumerateDevices` failure (no permission, etc.)
 *   - `hfpTriggerRisk === false` (default mic isn't Bluetooth)
 *   - No non-Bluetooth audioinput exists (laptop with only AirPods)
 *   - `defaultInputLabel` doesn't actually look Bluetooth (the
 *     `hfpTriggerRisk` predicate is groupId-based and can theoretically
 *     fire on a wired headset that exposes both an input and an
 *     output — we don't want to override in that case).
 *
 * Implementation deliberately separate from `MicCaptureSource` so
 * we can unit-test the picker logic without spinning up a real
 * MediaStream.
 */
async function pickNonBluetoothMicForHfpAvoidance(): Promise<{
  deviceId: string
  fromLabel: string
  toLabel: string
} | null> {
  // Opt-in feature. Off by default — the substitution is opinionated
  // (a power user with AirPods + a USB headset may not want us to
  // silently pick the USB one) and only a small subset of users hit
  // the HFP degradation it works around. Users with the issue can
  // enable "Avoid HFP microphones" in Settings → Meetings → Audio.
  if (!avoidHfpMicsEnabled()) return null

  const routing = await snapshotAudioRouting()
  if (!routing || !routing.hfpTriggerRisk) return null

  // Defensive: only override when the default input is actually a
  // Bluetooth device by label. The groupId-based `hfpTriggerRisk`
  // predicate can theoretically fire on wired hardware (e.g. a USB
  // headset exposing both input and output endpoints), and we don't
  // want to override those — they don't have the HFP-degradation
  // problem.
  if (!looksBluetoothLabel(routing.defaultInputLabel)) return null

  let devices: Array<MediaDeviceInfo>
  try {
    devices = await navigator.mediaDevices.enumerateDevices()
  } catch (err) {
    console.warn('[AudioTranscriptionService:mic-auto-switch] enumerateDevices failed; using default mic:', err)
    return null
  }

  if (devices.every((d) => d.kind !== 'audioinput' || d.label.length === 0)) {
    let probe: MediaStream | null = null
    try {
      probe = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      console.warn('[AudioTranscriptionService:mic-auto-switch] label-unmask probe rejected; using default mic:', error)
      publishAudioDebugEvent({
        atMs: Date.now(),
        channel: 'mic',
        severity: 'warn',
        message: 'mic permission probe rejected — HFP avoidance picker disabled',
      })
      return null
    } finally {
      probe?.getTracks().forEach((track) => track.stop())
    }
    try {
      devices = await navigator.mediaDevices.enumerateDevices()
    } catch (error) {
      console.warn('[AudioTranscriptionService:mic-auto-switch] enumerateDevices failed after probe:', error)
      return null
    }
  }

  const candidate = pickHfpFallbackMic(devices)
  if (!candidate) {
    // No safe alternative — log so the user understands why we're
    // about to engage HFP mode. The `routing.hfpTriggerRisk=TRUE`
    // event already published from `onAcquired` will tell them what
    // happened next.
    //
    // We intentionally do NOT silently fall back to e.g. an iPhone
    // Continuity Camera mic or a virtual audio device — yes, those
    // would keep system loopback in A2DP, but a meeting recorded
    // through the user's phone (held in another room) is far worse
    // than HFP-degraded loopback.
    console.warn(
      '[AudioTranscriptionService:mic-auto-switch] HFP trigger risk detected ' +
        `(default in/out both "${routing.defaultInputLabel}") but NO suitable built-in / wired mic ` +
        'is available — falling back to default. System loopback will likely degrade to AAC-ELD ' +
        '24 kHz mono until the underlying device routing changes.',
    )
    publishAudioDebugEvent({
      atMs: Date.now(),
      channel: 'mic',
      severity: 'error',
      message: `HFP risk: no built-in/wired mic available; keeping "${routing.defaultInputLabel}"`,
    })
    return null
  }

  // Loud log — useful even when the overlay is off (typical
  // production use case). The string is grep-friendly:
  // `[mic-auto-switch] override` finds every override decision.
  console.warn(
    '[AudioTranscriptionService:mic-auto-switch] override: default mic ' +
      `"${routing.defaultInputLabel}" is Bluetooth and also the default output ` +
      `(HFP trigger risk). Using "${candidate.label || candidate.deviceId}" (${candidate.tier}) ` +
      'instead to keep system loopback in A2DP. Set an explicit micDeviceId to opt out.',
  )
  return {
    deviceId: candidate.deviceId,
    fromLabel: routing.defaultInputLabel || '(default)',
    toLabel: candidate.label || candidate.deviceId,
  }
}

/**
 * Choose the best non-Bluetooth mic to substitute when the default
 * input is a Bluetooth headset that's also the default output. Returns
 * the first candidate that survives the filter, in priority order:
 *
 *   1. **Built-in laptop / desktop mic** (MacBook Pro Microphone,
 *      Microphone Array, etc.) — the safest, most predictable choice
 *      and what users overwhelmingly want.
 *   2. **Any other wired mic** (USB headset, audio interface) — only
 *      if no built-in is exposed. Still excludes iPhone Continuity
 *      Camera mics, iPad mics, virtual / loopback devices, and
 *      conferencing-app virtual mics, because silently capturing a
 *      meeting through any of those would be worse than HFP-degraded
 *      loopback.
 *
 * Returns `null` when neither tier has a survivor — caller falls back
 * to using the original Bluetooth default and accepts HFP degradation.
 *
 * Exported (and pure) for unit testing.
 */
export interface HfpFallbackCandidate {
  deviceId: string
  label: string
  tier: 'built-in' | 'wired-other'
}
export function pickHfpFallbackMic(devices: Array<MediaDeviceInfo>): HfpFallbackCandidate | null {
  // Exclude default/communications aliases and unlabeled devices: aliases
  // can still point back to Bluetooth, and an empty label cannot be
  // classified safely.
  const inputs = devices.filter(
    (d) =>
      d.kind === 'audioinput' &&
      d.deviceId !== 'default' &&
      d.deviceId !== 'communications' &&
      d.label.length > 0 &&
      !looksBluetoothLabel(d.label) &&
      !looksUnwantedAutoMicLabel(d.label),
  )
  const builtIn = inputs.find((d) => looksBuiltInMicLabel(d.label))
  if (builtIn) {
    return { deviceId: builtIn.deviceId, label: builtIn.label, tier: 'built-in' }
  }
  const other = inputs[0]
  if (other) {
    return { deviceId: other.deviceId, label: other.label, tier: 'wired-other' }
  }
  return null
}

// ── System loopback ────────────────────────────────────────────────

/**
 * System loopback must not use mic-oriented cleanup. AEC, AGC,
 * noise suppression, and voice isolation all distort or suppress the
 * far-end audio we are trying to transcribe.
 *
 * No `channelCount` / `sampleRate` constraints: the macOS CATAP
 * loopback advertises capabilities locked at `{min:48000, max:48000}`
 * for sampleRate and only honours stereo today, so any `ideal` hint
 * we send is silently dropped (verified via `track.getSettings()`
 * after acquisition). The Web Audio wrap that the base class wires
 * up handles downmix-to-mono and resample-to-TARGET_SAMPLE_RATE
 * inside Chromium's optimised C++ pipeline, so we don't need to
 * pre-shape the source.
 */
const SYSTEM_LOOPBACK_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  autoGainControl: false,
  noiseSuppression: false,
  // Cast through `unknown` because `voiceIsolation` isn't in lib.dom.d.ts.
  ...({ voiceIsolation: false } as unknown as Record<string, unknown>),
}

/**
 * System loopback capture via `navigator.mediaDevices.getDisplayMedia`.
 *
 * Audio-only displayMedia routes through Apple's Core Audio Tap API without
 * starting ScreenCaptureKit video capture.
 */
export class SystemCaptureSource extends AudioCaptureSource {
  readonly label: ChannelLabel = 'sys'

  // Use the base Web Audio wrap for downmix/resample. CATAP now ends the
  // stream on output-device rate changes, so re-acquiring is cleaner than
  // carrying a source-owned resampler and debug tap stack.
  protected async acquireStream(): Promise<MediaStream> {
    return navigator.mediaDevices.getDisplayMedia({
      audio: SYSTEM_LOOPBACK_AUDIO_CONSTRAINTS,
      video: false,
    })
  }

  protected async onAcquired(): Promise<void> {
    const tracks = this.stream?.getAudioTracks() ?? []
    if (tracks.length === 0) {
      console.warn('[AudioTranscriptionService:sys] No audio tracks from getDisplayMedia')
      return
    }

    // Belt-and-suspenders: even when getDisplayMedia accepts the
    // constraints object, some Chromium builds silently fall back to
    // default APM settings for loopback tracks. Re-applying the
    // constraints on the live track is the only reliable way to
    // ensure echoCancellation/AGC/NS stay off.
    //
    // OverconstrainedError is the expected rejection on Chromium
    // builds where the loopback track's constraints are read-only —
    // the track retains its initial (correct) constraints in that
    // case, so it's recoverable and we log at info-level. Anything
    // else (NotAllowedError, SecurityError, etc.) is real and worth
    // surfacing as a warning since it implies the loopback may now
    // be running with the OS-default APM settings (AEC/AGC/NS on),
    // which silently destroys the signal we want to transcribe.
    try {
      await tracks[0].applyConstraints(SYSTEM_LOOPBACK_AUDIO_CONSTRAINTS)
    } catch (err) {
      const name = (err as DOMException)?.name ?? ''
      if (name === 'OverconstrainedError') {
        console.log(
          '[AudioTranscriptionService:sys] applyConstraints rejected as OverconstrainedError — initial constraints retained',
        )
      } else {
        // Non-OverconstrainedError throws mean the live track is now
        // running with whatever APM the OS defaulted to — typically
        // AEC + AGC + NS, which silently destroys the loopback signal
        // we want to transcribe. Capture to Sentry so this isn't
        // invisible in field telemetry; surface in the debug overlay
        // so support sees it without having to grep the renderer log.
        console.warn(
          '[AudioTranscriptionService:sys] applyConstraints failed — loopback may be running with default APM (AEC/AGC/NS):',
          err,
        )
        publishAudioDebugEvent({
          atMs: Date.now(),
          channel: 'sys',
          severity: 'error',
          message: `applyConstraints rejected (${name || 'unknown'}) — loopback APM may be active, signal degraded`,
        })
        Sentry.captureException(err instanceof Error ? err : new Error(`applyConstraints failed: ${String(err)}`), {
          tags: {
            component: 'audio-transcription-service',
            channel: 'sys',
            errorId: 'audio-loopback-apm-uncontrolled',
          },
        })
      }
    }

    // Log everything the renderer can see about the loopback track
    // so we can tell at a glance which CATAP source it landed on AND
    // confirm that AEC/AGC/NS are actually disabled.
    try {
      const sysTrack = tracks[0]
      const settings = sysTrack.getSettings() as Record<string, unknown>
      const capabilities = typeof sysTrack.getCapabilities === 'function' ? sysTrack.getCapabilities() : {}
      const settingsJson = JSON.stringify(settings, (_k, v) => (typeof v === 'bigint' ? v.toString() : v))
      console.log(
        `[AudioTranscriptionService:sys] loopback track: label="${sysTrack.label}" id="${sysTrack.id}" deviceId=${settings.deviceId ?? '(unset)'} groupId=${settings.groupId ?? '(unset)'} sampleRate=${settings.sampleRate ?? '(unset)'} channelCount=${settings.channelCount ?? '(unset)'} settings=${settingsJson} capabilities=${JSON.stringify(capabilities)}`,
      )
    } catch (err) {
      console.warn('[AudioTranscriptionService:sys] Failed to log system track settings:', err)
    }
    // CATAP captures the current default output; log the routing so HFP
    // risk and output-device changes are visible next to acquisition.
    const routing = await snapshotAudioRouting()
    console.log(`[AudioTranscriptionService:sys] audio routing at acquire: ${formatAudioRouting(routing)}`)
    if (routing) {
      publishAudioDebugRouting(routing)
      publishAudioDebugEvent({
        atMs: Date.now(),
        channel: 'sys',
        severity: routing.hfpTriggerRisk ? 'warn' : 'info',
        message: routing.hfpTriggerRisk
          ? `system loopback acquired with HFP trigger risk (output is "${routing.defaultOutputLabel}", same hardware as default input)`
          : `system loopback acquired (output is "${routing.defaultOutputLabel}")`,
      })
    }
  }
}

/**
 * Stable string fingerprint of currently-enumerated `audiooutput`
 * devices. Identical when the OS hasn't changed the available output
 * set; differs when the user (or macOS itself) adds/removes/relabels
 * an output — which is the signal we use to detect AirPods
 * disconnect, USB DAC unplug, and other endpoint swaps that silently
 * wedge the CATAP tap.
 */
export async function snapshotAudioOutputs(): Promise<string> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices
      .filter((d) => d.kind === 'audiooutput')
      .map((d) => `${d.deviceId}:${d.label}:${d.groupId}`)
      .sort(compareCodeUnits)
      .join('|')
  } catch (err) {
    console.warn('[AudioTranscriptionService] snapshotAudioOutputs failed:', err)
    return ''
  }
}
